// ADR-0158 (fase 3, #0760) — fotos y PDF en el chat de la consulta y
// mensajes casi en vivo.
//
// Los archivos viven en el bucket privado (ADR-0157, kind `inquiries`,
// scopeId = inquiryId). El hilo sólo trae sus metadatos (id, tipo,
// contentType, tamaño): las URL firmadas se piden aparte, en lote, para
// que la revisión periódica no llame a media-svc ni cambie el `src` de
// las imágenes.
//
//   POST /assets/:id/inquiries/:inquiryId/attachments/upload-url → presign (propietario)
//   POST /owner-inquiries/:id/attachments/upload-url              → presign (staff, sólo PDF)
//   GET  /assets/:id/inquiries/:inquiryId/attachments/urls?ids=   → lectura firmada (propietario)
//   GET  /owner-inquiries/:id/attachments/urls?ids=               → lectura firmada (staff)
//
// Los adjuntos se registran en los POST …/messages que ya existen (el
// cuerpo suma `attachments: [{ objectKey }]`). Este archivo no importa
// owner-inquiries.ts: es al revés, para no armar un ciclo.
import { z } from "zod";
import { privatePresignUploadResponseSchema } from "./media.js";
export const OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX = 5;
export const OWNER_INQUIRY_ATTACHMENTS_PER_INQUIRY_MAX = 50;
/** Igual a MAX_UPLOAD_BYTES de media-svc y a DOCUMENT_MAX_BYTES. */
export const OWNER_INQUIRY_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const OWNER_INQUIRY_OWNER_ATTACHMENT_CONTENT_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf",
];
/** El staff sólo adjunta PDF: así el dashboard no necesita comprimir
 *  fotos. Lo impone el servidor por carril y un CHECK lo respalda. */
export const OWNER_INQUIRY_STAFF_ATTACHMENT_CONTENT_TYPES = ["application/pdf"];
/** El tope de un lote de /internal/private/presign-download. */
export const OWNER_INQUIRY_ATTACHMENT_URLS_BATCH_MAX = 24;
/** Presign del staff, por minuto y por cuenta. */
export const OWNER_INQUIRY_ATTACHMENT_UPLOAD_RATE_LIMIT = 30;
export const OWNER_INQUIRY_ATTACHMENT_URL_RATE_LIMIT = 60;
/** Presign del propietario (el único rol externo que recibe un PUT al
 *  bucket privado), por HORA y por cuenta: 20 mensajes de 5 archivos.
 *  Acota los objetos huérfanos que una cuenta puede dejar sin enviar
 *  nunca un mensaje (ADR-0158, «Conservación»). */
export const OWNER_INQUIRY_OWNER_ATTACHMENT_UPLOAD_HOURLY_LIMIT = 100;
/** Cada cuánto se revisa el hilo abierto (PWA y dashboard), sólo con la
 *  pestaña visible. Fuente única. */
export const OWNER_INQUIRY_THREAD_POLL_MS = 15_000;
export const OWNER_INQUIRY_ATTACHMENT_TYPE_NOT_ALLOWED = "ATTACHMENT_TYPE_NOT_ALLOWED";
export const OWNER_INQUIRY_ATTACHMENT_SIZE_INVALID = "ATTACHMENT_SIZE_INVALID";
export const OWNER_INQUIRY_ATTACHMENT_LIMIT_EXCEEDED = "ATTACHMENT_LIMIT_EXCEEDED";
export const OWNER_INQUIRY_ATTACHMENT_ALREADY_REGISTERED = "ATTACHMENT_ALREADY_REGISTERED";
export const OWNER_INQUIRY_ATTACHMENT_DUPLICATE = "ATTACHMENT_DUPLICATE";
export const OWNER_INQUIRY_ATTACHMENT_NOT_FOUND = "ATTACHMENT_NOT_FOUND";
/** Un adjunto tal como viaja en el hilo. Sin URL (se pide aparte) y sin
 *  nombre original del archivo (puede traer datos personales): el
 *  cliente rotula «Foto 1», «PDF 1». */
export const ownerInquiryAttachmentSchema = z.object({
    id: z.string().uuid(),
    tipo: z.enum(["foto", "pdf"]),
    contentType: z.enum(OWNER_INQUIRY_OWNER_ATTACHMENT_CONTENT_TYPES),
    sizeBytes: z.number().int().positive().max(OWNER_INQUIRY_ATTACHMENT_MAX_BYTES),
});
/** POST …/attachments/upload-url. La lista por carril (el staff sólo
 *  PDF) la impone el servidor. */
export const ownerInquiryAttachmentUploadUrlRequestSchema = z
    .object({
    contentType: z.enum(OWNER_INQUIRY_OWNER_ATTACHMENT_CONTENT_TYPES),
    contentLength: z.number().int().positive().max(OWNER_INQUIRY_ATTACHMENT_MAX_BYTES),
})
    .strict();
export const ownerInquiryAttachmentUploadUrlResponseSchema = privatePresignUploadResponseSchema;
/** Un adjunto dentro del cuerpo de POST …/messages: la clave que acuñó
 *  nuestro presign. El servidor la ata a la org y a la consulta y la
 *  confirma con un HEAD firmado antes de registrarla. */
export const ownerInquiryAttachmentRefSchema = z
    .object({
    objectKey: z.string().min(1).max(512),
})
    .strict();
/** GET …/attachments/urls?ids=a,b,c — entre 1 y 24 ids, sin repetir. */
export const ownerInquiryAttachmentUrlsQuerySchema = z
    .object({
    ids: z
        .string()
        .min(1)
        .transform((raw) => raw.split(",").map((s) => s.trim()))
        .pipe(z
        .array(z.string().uuid())
        .min(1)
        .max(OWNER_INQUIRY_ATTACHMENT_URLS_BATCH_MAX)
        .refine((ids) => new Set(ids).size === ids.length, { message: "ids repetidos" })),
})
    .strict();
export const ownerInquiryAttachmentUrlsResponseSchema = z.object({
    urls: z.array(z.object({
        id: z.string().uuid(),
        url: z.string().url(),
        expiresAt: z.string().datetime(),
    })),
});
/** «pdf» o «foto», según el Content-Type registrado. */
export function attachmentTipo(contentType) {
    return contentType === "application/pdf" ? "pdf" : "foto";
}
//# sourceMappingURL=owner-inquiry-attachments.js.map